/**
 * Support/Contact Us Management Page
 * Displays contact form submissions with pagination and delete functionality
 */

import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToastNotifications } from '../hooks/useToast';
import { realAPI } from '../api/realEndpoints';
import { formatDate } from '../utils/helpers';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';

// Support contact types
interface SupportContact {
  id: number;
  userId: number;
  email: string;
  description: string;
  createdAt: string;
}

interface SupportResponse {
  count: number;
  results: SupportContact[];
}

export default function Support() {
  const { success, error } = useToastNotifications();
  const [contacts, setContacts] = useState<SupportContact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<SupportContact | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortField, setSortField] = useState<'email' | 'description' | 'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch support contacts
  const fetchContacts = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * limit;
      const response = await realAPI.support.getContacts(limit, offset, search, sortField, sortOrder);

      if (!response) {
        setContacts([]);
        setTotal(0);
        return;
      }

      setContacts(response.results || []);
      setTotal(response.count || 0);
    } catch (err) {
      console.error('Error fetching support contacts:', err);
      setContacts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, search, sortField, sortOrder]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  // Handle delete click
  const handleDeleteClick = (contact: SupportContact) => {
    setContactToDelete(contact);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    setIsDeleting(true);
    try {
      await realAPI.support.deleteContact(contactToDelete.id);
      await fetchContacts();
      setIsDeleteModalOpen(false);
      setContactToDelete(null);
      success('Contact deleted successfully', 'Contact submission has been removed');
    } catch (err: any) {
      console.error('Failed to delete contact:', err);
      error('Failed to delete contact', 'Please try again later');
    } finally {
      setIsDeleting(false);
    }
  };

  // Table shimmer component
  const TableShimmer = () => (
    <div className="table-container">
      <div className="table-min-w">
        <div className="grid grid-cols-5 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-sm border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
          <div className="text-gray-700 dark:text-white col-span-1" style={{ paddingLeft: '10px' }}>Email</div>
          <div className="text-gray-700 dark:text-white col-span-2" style={{ paddingLeft: '6px' }}>Description</div>
          <div className="text-gray-700 dark:text-white text-center">Created</div>
          <div className="text-gray-700 dark:text-white text-center">Actions</div>
        </div>

        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Support Management</h1>
      </div>



      {/* Contacts Table */}
      <Card>
        {isLoading ? (
          <TableShimmer />
        ) : contacts.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-5 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-sm border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-1"
                  style={{ paddingLeft: '10px' }}
                  onClick={() => {
                    if (sortField === 'email') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('email');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Email {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-2"
                  style={{ paddingLeft: '6px' }}
                  onClick={() => {
                    if (sortField === 'description') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('description');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Description {sortField === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white text-center"
                  onClick={() => {
                    if (sortField === 'createdAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('createdAt');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  Created {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div className="text-center dark:text-white">Actions</div>
              </div>
              {/* Empty State inside table */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className=" mx-auto mb-4 " />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <div className="table-min-w">
                {/* Grid Header */}
                <div className="grid grid-cols-5 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-sm border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-1"
                    style={{ paddingLeft: '10px' }}
                    onClick={() => {
                      if (sortField === 'email') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('email');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Email {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white col-span-2"
                    style={{ paddingLeft: '6px' }}
                    onClick={() => {
                      if (sortField === 'description') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('description');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Description {sortField === 'description' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white text-center"
                    onClick={() => {
                      if (sortField === 'createdAt') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('createdAt');
                        setSortOrder('desc');
                      }
                      setPage(1);
                    }}
                  >
                    Created {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                  <div className="text-center dark:text-white">Actions</div>
                </div>
                {/* Grid Body */}
                <div className="divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="grid grid-cols-5 gap-4 p-2 hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors items-center cursor-pointer"
                    >
                      <div className="font-medium col-span-1">
                        <a
                          href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
                            contact.email
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate dark:text-white text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {contact.email}
                        </a>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 col-span-2">
                        <span className="truncate line-clamp-3">{contact.description}</span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 text-center">
                        {formatDate(contact.createdAt)}
                      </div>
                      <div>
                        <div className="flex justify-center gap-3">
                          <button
                            onClick={() => handleDeleteClick(contact)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-6 h-6" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} contacts
          </div>
          <div className="flex gap-2">
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700">
                Page {page} of {Math.ceil(total / limit)}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page * limit >= total}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Delete Contact" size="sm">
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete this contact from {contactToDelete?.email}? This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
